import styled from '@emotion/styled';
import { CheckBox, CheckBoxOutlineBlank, Warning } from '@mui/icons-material';
import { Skeleton } from '@mui/material';
import { useEffect, useMemo } from 'react';
import CountUp from 'react-countup';
import { Flag } from '~/modules/Elements/Flag';
import { MenuButton } from '~/modules/Elements/Menu';
import MenuWithLogo from '~/modules/Elements/MenuWithLogo';
import { useLanguageList } from '~/modules/Songs/hooks/useLanguageList';
import useSongIndex from '~/modules/Songs/hooks/useSongIndex';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import isE2E from '~/modules/utils/isE2E';
import languageNameToIsoCode from '~/modules/utils/languageNameToIsoCode';
import { ExcludedLanguagesSetting, useSettingValue } from '~/routes/Settings/SettingsState';

interface Props {
  onClose: () => void;
  closeText: string;
}

const MIN_SONGS_COUNT = isE2E() ? 0 : 20;

function ExcludeLanguagesView({ onClose, closeText }: Props) {
  const { register } = useKeyboardNav({ onBackspace: onClose });

  const [excludedLanguages, setExcludedLanguages] = useSettingValue(ExcludedLanguagesSetting);
  const { data, isLoading } = useSongIndex();
  const availableLanguages = useLanguageList(data);

  const languageList = useMemo(
    () => availableLanguages.filter(({ name, count }) => languageNameToIsoCode(name) && count >= MIN_SONGS_COUNT),
    [availableLanguages],
  );

  const otherSongCount = useMemo(
    () =>
      availableLanguages
        .filter(({ name }) => !languageList.find((lang) => lang.name === name))
        .reduce((acc, { count }) => acc + count, 0),
    [availableLanguages, languageList],
  );

  const songCount = languageList
    .filter(({ name }) => !excludedLanguages?.includes(name))
    .reduce((acc, { count }) => acc + count, 0);

  const toggleLanguage = (language: string) => {
    if (excludedLanguages === null) setExcludedLanguages([language]);
    else if (!excludedLanguages.includes(language)) setExcludedLanguages([...excludedLanguages, language]);
    else setExcludedLanguages(excludedLanguages.filter((lang) => lang !== language));
  };

  useEffect(() => {
    if (excludedLanguages === null && navigator?.languages) {
      const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });
      const preferredLanguages = navigator?.languages
        .map((lang) => languageNames.of(lang)?.toLowerCase())
        .filter(Boolean) as string[];

      const toExclude = languageList
        .map((lang) => lang.name)
        .filter((lang) => !preferredLanguages.some((preferred) => preferred.includes(lang.toLowerCase())))
        .filter((lang) => lang !== 'English'); // Always have English selected as default

      if (toExclude.length) setExcludedLanguages(toExclude);
    }
  }, [excludedLanguages, languageList]);

  const areAllLanguagesExcluded = useMemo(
    () => languageList.every((language) => excludedLanguages?.includes(language.name)),
    [excludedLanguages, languageList],
  );

  useEffect(() => {
    if (excludedLanguages?.find((language) => !languageList.find((lang) => lang.name === language))) {
      setExcludedLanguages(excludedLanguages.filter((language) => languageList.find((lang) => lang.name === language)));
    }
  }, [languageList, excludedLanguages, setExcludedLanguages]);

  return (
    <MenuWithLogo
      showLogo={false}
      showGithubRibbon={false}
      supportedBrowsers={false}
      backgroundImage="/menu_background_blur.png"
      backgroundOverlay={true}
      toolbar={true}>
      <ScaleWrap>
        <Title>Select Song Languages</Title>

        <LanguageListContainer>
          {isLoading &&
            new Array(6).fill(0).map((_, i) => (
              <Skeleton variant="rectangular" width="100%" height="7.5rem" key={i}>
                <LanguageEntry data-excluded focused={false}>
                  <Flag language={['English']} />
                </LanguageEntry>
              </Skeleton>
            ))}

          {languageList.map(({ name, count }) => {
            const excluded = excludedLanguages?.includes(name) ?? false;

            return (
              <LanguageEntry key={name} data-excluded={excluded} {...register(`lang-${name}`, () => toggleLanguage(name))}>
                <Check aria-hidden="true">{excluded ? <CheckBoxOutlineBlank /> : <CheckBox />}</Check>

                <LanguageText>
                  <LanguageName>{name}</LanguageName>
                  <LanguageCount>({count} songs)</LanguageCount>
                </LanguageText>

                <LanguageFlagBackground data-excluded={excluded} aria-hidden="true">
                  <Flag language={[name]} />
                </LanguageFlagBackground>
              </LanguageEntry>
            );
          })}

          {otherSongCount > 0 && (
            <Disclaimer>
              …and <strong>{otherSongCount} songs</strong> in other languages
            </Disclaimer>
          )}
        </LanguageListContainer>

        <Hint>
          You can always update the selection in <strong>Manage Songs</strong> menu
        </Hint>

        <NextButtonContainer>
          <MenuButton
            {...register('close-exclude-languages', onClose, undefined, true, {
              disabled: areAllLanguagesExcluded || isLoading,
            })}>
            {closeText}
          </MenuButton>

          <Disclaimer>
            The list will contain{' '}
            <strong>
              <CountUp duration={1} preserveValue end={songCount + otherSongCount} />
            </strong>{' '}
            songs
          </Disclaimer>

          {areAllLanguagesExcluded && (
            <Disclaimer data-test="all-languages-excluded-warning">
              <strong>
                <Warning />
              </strong>{' '}
              You excluded all the languages, pick at least one
            </Disclaimer>
          )}
        </NextButtonContainer>
      </ScaleWrap>
    </MenuWithLogo>
  );
}

/**
 * IMPORTANT:
 * - Desktop: subimos ~20% (sin romper nada).
 * - Móvil: subimos muchísimo (porque tu “modo móvil” global lo está encogiendo todo).
 * Si después ves que se pasa, te digo qué números bajar.
 */
const ScaleWrap = styled.div`
  /* Desktop ~ +20% */
  font-size: 1.2em;

  /* Móvil: “contraatacamos” el shrink global */
  @media (max-width: 520px) {
    font-size: 2.35em; /* ~4-5x respecto a cómo lo veías ahora */
  }
`;

/* -------------------- layout / typography -------------------- */

const Title = styled.h1`
  margin: 0.5rem 0 0;
  text-align: center;
  font-size: clamp(1.6rem, 2.2vw, 2.4rem);

  @media (max-width: 520px) {
    font-size: clamp(1.9rem, 6vw, 2.6rem);
  }
`;

const Hint = styled.h3`
  margin: 1rem 0 0;
  text-align: center;
  font-size: clamp(1.05rem, 1.4vw, 1.25rem);

  @media (max-width: 520px) {
    font-size: clamp(1.05rem, 4.2vw, 1.35rem);
    padding: 0 0.25rem;
  }
`;

const NextButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const Disclaimer = styled.h4`
  margin: 0;
  text-align: right;
  font-weight: 400;
  font-size: clamp(0.95rem, 1.1vw, 1.1rem);

  @media (max-width: 520px) {
    text-align: center;
    font-size: clamp(0.95rem, 3.6vw, 1.2rem);
  }
`;

const LanguageListContainer = styled.div`
  margin-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

/* -------------------- row content -------------------- */

const Check = styled.div`
  flex: 0 0 auto;

  svg {
    width: clamp(2.6rem, 2.2vw, 3.1rem);
    height: clamp(2.6rem, 2.2vw, 3.1rem);
  }

  @media (max-width: 520px) {
    svg {
      width: clamp(2.4rem, 7.2vw, 3.4rem);
      height: clamp(2.4rem, 7.2vw, 3.4rem);
    }
  }
`;

const LanguageText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  overflow: hidden;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.15rem;
  }
`;

const LanguageName = styled.span`
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: clamp(1.25rem, 1.6vw, 1.65rem);

  @media (max-width: 520px) {
    font-size: clamp(1.2rem, 5.2vw, 1.8rem);
  }
`;

const LanguageCount = styled.span`
  opacity: 0.9;
  white-space: nowrap;
  font-size: clamp(1.05rem, 1.3vw, 1.35rem);

  @media (max-width: 520px) {
    font-size: clamp(1.05rem, 4.4vw, 1.45rem);
  }
`;

const LanguageFlagBackground = styled.div`
  transition: 300ms;
  opacity: 0.95;

  &[data-excluded='true'] {
    filter: grayscale(0.6);
    opacity: 0.45;
  }

  position: absolute;
  right: clamp(0.7rem, 1vw, 0.95rem);
  top: 50%;
  transform: translateY(-50%);
  overflow: hidden;
  border-radius: 0.9rem;

  /* Desktop: más grande (+20% aprox) */
  width: clamp(13.5rem, 14vw, 16.5rem);
  height: clamp(6.2rem, 6.5vw, 7.4rem);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Móvil: MUCHO más grande */
  @media (max-width: 520px) {
    border-radius: 0.95rem;
    width: clamp(12rem, 46vw, 16rem);
    height: clamp(6.2rem, 22vw, 8.8rem);
  }
`;

const LanguageEntry = styled(MenuButton)`
  position: relative;
  width: 100%;

  justify-content: flex-start;
  align-items: center;
  gap: 1.25rem;

  /* Reservamos sitio para la bandera (en desktop) */
  padding-left: 1.25rem;
  padding-right: clamp(16rem, 17vw, 19rem);

  /* Desktop: un poco más alto */
  height: clamp(7.1rem, 7vw, 8.6rem);
  font-size: 1rem; /* el tamaño lo gobierna ScaleWrap + clamps */
  margin: 0;

  &[data-excluded='true'] {
    text-decoration: line-through white;
    opacity: 0.6;

    &[data-focused='false'] {
      background: rgba(0, 0, 0, 0.55);
    }
  }

  /* Móvil: tarjeta alta, dedo-friendly, y la bandera es grande */
  @media (max-width: 520px) {
    gap: 0.85rem;

    height: clamp(8.8rem, 18vw, 11.2rem);

    padding-left: 1rem;
    padding-right: clamp(13.5rem, 50vw, 17rem);
  }
`;

export default ExcludeLanguagesView;
